import { axiosV2 } from "config/@axios";
import idFixer from "utils/idFixer";
import { getReminders, getTaskMessageRef } from "pages/Mail/helpers/reminders";

export const SET_MESSAGE_REMINDERS = "reminderActions::setMessageReminders";
export const SET_CONVERSATION_REMINDER =
  "reminderActions::setConversationReminder";

const messageRequests = new Map();
const conversationRequests = new Map();

const nextRequest = (registry, id) => {
  const token = (registry.get(id) || 0) + 1;
  registry.set(id, token);
  return token;
};

const isLatestRequest = (registry, id, token) => registry.get(id) === token;

export const setMessageReminders = ({ id, has_reminder, reminders }) => ({
  type: SET_MESSAGE_REMINDERS,
  payload: { id: idFixer(id), has_reminder, reminders }
});

export const setConversationReminder = ({ id, has_reminder }) => ({
  type: SET_CONVERSATION_REMINDER,
  payload: { id: idFixer(id), has_reminder }
});

const includesId = (ids, id) =>
  Array.isArray(ids) && ids.some(item => idFixer(item) === id);

export const isMessageInStore = (entities, id) =>
  !!(
    entities?.messages?.byId?.[id] ||
    entities?.singleMessage?.byId?.[id] ||
    entities?.conversations?.byId?.[id]
  );

export const findConversationRowIds = (
  messagesById,
  messageId,
  conversationId
) =>
  Object.keys(messagesById || {})
    .filter(key => {
      const row = messagesById[key];
      if (row?.listingType !== "conversation") {
        return false;
      }

      if (
        idFixer(row.id) === messageId ||
        includesId(row.children, messageId) ||
        includesId(row.conversations, messageId)
      ) {
        return true;
      }

      if (!conversationId) {
        return false;
      }

      return (
        idFixer(row.conversation_id || row.conversation_id_other) ===
        conversationId
      );
    })
    .map(key => idFixer(key));

const normalizeHasReminder = (hasReminderValue, reminders) =>
  parseInt(hasReminderValue) === 1 || reminders.length > 0 ? 1 : 0;

const syncConversationRow = id => async dispatch => {
  const token = nextRequest(conversationRequests, id);

  try {
    const response = await axiosV2.get(`/conversations/${id}`);

    if (!isLatestRequest(conversationRequests, id, token)) {
      return;
    }

    dispatch(
      setConversationReminder({
        id,
        has_reminder: parseInt(response.data?.has_reminder) === 1 ? 1 : 0
      })
    );
  } catch (err) {
    return;
  }
};

export const syncMessageReminders =
  ({ messageId }) =>
  async (dispatch, getState) => {
    const id = idFixer(messageId);

    if (!id || !isMessageInStore(getState().entities, id)) {
      return;
    }

    const token = nextRequest(messageRequests, id);

    let data;
    try {
      const response = await axiosV2.get(`/messages/${id}`);
      data = response.data;
    } catch (err) {
      return;
    }

    if (!isLatestRequest(messageRequests, id, token)) {
      return;
    }

    const reminders = getReminders(data);
    const has_reminder = normalizeHasReminder(data?.has_reminder, reminders);

    dispatch(setMessageReminders({ id, has_reminder, reminders }));

    const conversationId =
      idFixer(data?.conversation_id || data?.conversation_id_other) || null;
    const rowIds = findConversationRowIds(
      getState().entities.messages.byId,
      id,
      conversationId
    );

    await Promise.all(
      rowIds.map(rowId => {
        if (has_reminder === 1) {
          nextRequest(conversationRequests, rowId);
          dispatch(setConversationReminder({ id: rowId, has_reminder: 1 }));
          return Promise.resolve();
        }

        return dispatch(syncConversationRow(rowId));
      })
    );
  };

export const syncTaskMessageReminders = task => dispatch => {
  const ref = getTaskMessageRef(task);

  if (!ref) {
    return Promise.resolve();
  }

  return dispatch(syncMessageReminders(ref));
};
